// Angular Files
import { Injectable } from '@angular/core';

// Other External Files
import { firstValueFrom, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

// Teller Online Files
import {
    AdminPaymentsApiClient,
    PaymentAdminDetailsDto,
    PaymentsNextPrevDto,
    PaymentsSearchOptionsEnumDto,
    RefundPaymentRequestDto,
    CancelPaymentRequestDto
} from 'apps/admin-portal/src/app/core/api/AdminPortalApiClients';
import {
    ScheduledPaymentDetailModel
} from 'apps/admin-portal/src/app/core/services/admin-scheduled-payments.service';

// Teller Online Library Files
import {
    PaymentStatusEnum,
    PaymentStatusEnumConvertor,
    PaymentDetailsModel,
    PaymentOverviewModel,
    SearchFilterModel,
    SearchFilterGroupEnum,
    SearchFilterOption,
    SearchSettingsModel,
    TellerOnlineAppService,
    TellerOnlineDataSourceCompatible,
    TellerOnlineFilteredResponseModel,
    TellerOnlinePaymentsService,
    TellerOnlineSiteMetadataService,
    PaymentMethodTypeEnum
} from 'teller-online-libraries/core';
import { TellerOnlineMessageService } from 'teller-online-libraries/shared';

@Injectable({
    providedIn: 'root'
})
export class AdminPaymentsService
    implements TellerOnlineDataSourceCompatible<PaymentOverviewModel, TellerOnlineFilteredPaymentsResponseModel> {
    constructor(
        private adminPaymentsApiClient: AdminPaymentsApiClient,
        private messageService: TellerOnlineMessageService,
        private paymentsService: TellerOnlinePaymentsService,
        private appService: TellerOnlineAppService,
        private siteMetadataService: TellerOnlineSiteMetadataService
    ) { }

    public getPaymentAdminStatus(paymentIdentifier: string): Promise<PaymentAdminDetailsModel> {
        return this.adminPaymentsApiClient.getPaymentAdminStatus(paymentIdentifier).toPromise();
    }

    public loadDataSource(
        settings: SearchSettingsModel,
        param?: { isUpcoming?: boolean }
    ): Observable<TellerOnlineFilteredPaymentsResponseModel> {
        const apiCall = param?.isUpcoming
            ? this.adminPaymentsApiClient
                .getUpcomingPayments(settings.toDto())
            : this.adminPaymentsApiClient
                .getFilteredPayments(settings.toDto());

        return apiCall
            .pipe(map(response => new TellerOnlineFilteredPaymentsResponseModel({
                    results: response.results.map(p => {
                        const overview = new PaymentOverviewModel(p as any);
                        overview.flatten();
                        return overview;
                    }),
                    totalResults: response.totalResults
                })));
    }

    public getErroredPayments(): Promise<PaymentOverviewModel[]> {
        const settings = new SearchSettingsModel();
        settings.addFilter(ERROR_PAYMENTS_FILTER);
        settings.pageSize = 5; // only pull the first 5
        return this.adminPaymentsApiClient.getFilteredPayments(settings.toDto()).pipe(map(response => response.results.map(p => {
                const overview = new PaymentOverviewModel(p as any);
                overview.flatten();
                return overview;
            }))).toPromise();
    }

    /**
     * Get the number of successful payments in the current month
     * @returns Total number of finalized payments in the current month
     */
    public getMonthlyPaymentsCount(): Promise<number> {
        const date = new Date();
        const settings = new SearchSettingsModel();

        // Setup a filter for payments in the last month
        settings.addFilter({
            label: 'Monthly Payments',
            filters: [
                new SearchFilterModel({
                    type: PaymentsSearchOptionsEnumDto.StartDate,
                    value: new Date(date.getFullYear(), date.getMonth()).toDateString()
                }),
                new SearchFilterModel({
                    type: PaymentsSearchOptionsEnumDto.Status,
                    value: PaymentStatusEnumConvertor.toDto(PaymentStatusEnum.Finalized)
                })
            ],
            filterGroup: SearchFilterGroupEnum.PaymentDate
        });

        // This gives just the totalCount, no actual results in the response
        settings.pageSize = 0;

        return this.adminPaymentsApiClient.getFilteredPayments(settings.toDto())
            .pipe(map(response => response.totalResults)).toPromise();
    }

    public getNextPrevPayments(paymentIdentifier: string, settings: SearchSettingsModel): Promise<PaymentNextPrevModel> {
        return this.adminPaymentsApiClient.getNextPrevPayments(paymentIdentifier, settings.toDto()).toPromise();
    }

    public syncPaymentWithTeller(paymentIdentifier: string) {
        return this.adminPaymentsApiClient.syncPaymentWithTeller(paymentIdentifier).toPromise();
    }

    public cancelPayment(paymentIdentifier: string, request: CancelPaymentRequestModel) {
        return this.adminPaymentsApiClient.cancelPayment(paymentIdentifier, request).toPromise();
    }

    public retryPayment(paymentIdentifier: string, request: CancelPaymentRequestModel) {
        return this.adminPaymentsApiClient.retryPayment(paymentIdentifier, request).toPromise();
    }

    public markPaymentRefunded(paymentIdentifier: string, request: RefundPaymentRequestModel) {
        return this.adminPaymentsApiClient.markPaymentRefunded(paymentIdentifier, request).toPromise();
    }

    public async modifyPayment(paymentIdentifier: string, action: ModifyAction, comments: string = null):
        Promise<{ paymentDetails: PaymentDetailsModel, adminPaymentStatus: PaymentAdminDetailsModel }> {
        try {
            this.appService.triggerPageLoading();

            if (action !== ModifyAction.Sync) {
                if (!comments) {
                    throw new Error('Comments are required.');
                }
            }

            switch (action) {
                case ModifyAction.Sync:
                    await this.syncPaymentWithTeller(paymentIdentifier);
                    break;
                case ModifyAction.Cancel:
                    await this.cancelPayment(paymentIdentifier, new RefundPaymentRequestModel({ comments }));
                    break;
                case ModifyAction.Retry:
                    await this.retryPayment(paymentIdentifier, new RefundPaymentRequestModel({ comments }));
                    break;
                case ModifyAction.Refund:
                    await this.markPaymentRefunded(paymentIdentifier, new CancelPaymentRequestModel({ comments }));
                    break;
                default:
                    break;
            }
            this.messageService.notification('The requested action was completed successfully.', 'success', 5000);
        } catch (e) {
            if (e?.errorDef === 'CannotSyncCartInvalidStatus') {
                throw e;
            }
            this.messageService.notification('The payment was not Finalized. '
                + 'Please check the Activity tab for more details.', 'caution', 5000);
            // Don't do anything with the error, we just don't want to show it because it's catered towards the Public Portal
        } finally {
            this.appService.finishPageLoading();
        }

        // Refresh the cart and return the details to be used by the calling method
        const paymentDetails = await this.paymentsService.getPaymentDetails(paymentIdentifier);
        const adminPaymentStatus = await this.getPaymentAdminStatus(paymentIdentifier);

        return {
            paymentDetails,
            adminPaymentStatus
        };
    }

    public getConvenienceFeeLabel(paymentMethodType: PaymentMethodTypeEnum, configKey?: string) {
        const convenienceFeeConfigs = this.siteMetadataService.appConfiguration.convenienceFeeConfig;
        const config = convenienceFeeConfigs.find(c => c.configKey ?? configKey === '');

        let label;

        // Grab tender specific label
        switch (paymentMethodType) {
            case PaymentMethodTypeEnum.CreditCard:
                label = config.creditConfig.convenienceFeeLabel;
                break;
            case PaymentMethodTypeEnum.ECheck:
                label = config.eCheckConfig.convenienceFeeLabel;
                break;
            default:
                break;
        }

        // Default label is always "Convenience Fee"
        return label ?? 'Convenience Fee';
    }

    public getScheduleByPaymentIdentifier(
        paymentIdentifier: string
    ): Promise<ScheduledPaymentDetailModel> {
        return firstValueFrom(
            this.adminPaymentsApiClient
                .getScheduledPaymentByPaymentIdentifier(
                    paymentIdentifier)
                .pipe(map(
                    dto => (dto
                        ? new ScheduledPaymentDetailModel(dto)
                        : null))));
    }
}

export enum ModifyAction {
    Sync,
    Cancel,
    Retry,
    Refund
}

export const ERROR_PAYMENTS_FILTER = {
    label: 'Errored',
    filters: [
        new SearchFilterModel({
            type: PaymentsSearchOptionsEnumDto.OneOfStatus,
            value: JSON.stringify([
                PaymentStatusEnumConvertor.toDto(PaymentStatusEnum.PendingReview),
                PaymentStatusEnumConvertor.toDto(PaymentStatusEnum.ProcessingFailed),
                PaymentStatusEnumConvertor.toDto(PaymentStatusEnum.PostingFailed),
            ])
        })
    ],
    filterGroup: SearchFilterGroupEnum.StatusGroup
};

export const TODAY_FILTER = {
    label: 'Today',
    filters: [
        new SearchFilterModel({
            type: PaymentsSearchOptionsEnumDto.StartDate,
            value: (new Date()).toDateString()
        })
    ],
    filterGroup: SearchFilterGroupEnum.PaymentDate
};

export const DATE_RANGE_FILTER: SearchFilterOption = {
    label: 'Date Range',
    filters: [],
    filterGroup: SearchFilterGroupEnum.PaymentDate
};

export const BEFORE_DATE_FILTER: SearchFilterOption = {
    label: 'Before',
    filters: [],
    filterGroup: SearchFilterGroupEnum.PaymentDate
};

export const AFTER_DATE_FILTER: SearchFilterOption = {
    label: 'After',
    filters: [],
    filterGroup: SearchFilterGroupEnum.PaymentDate
};

export const SINGLE_DAY_FILTER: SearchFilterOption = {
    label: 'Single Day',
    filters: [],
    filterGroup: SearchFilterGroupEnum.PaymentDate
};

export const AMOUNT_RANGE_FILTER: SearchFilterOption = {
    label: 'Amount Range',
    filters: [],
    filterGroup: SearchFilterGroupEnum.Amount
};

export class RefundPaymentRequestModel extends RefundPaymentRequestDto { }
export class CancelPaymentRequestModel extends CancelPaymentRequestDto { }

export class PaymentAdminDetailsModel extends PaymentAdminDetailsDto { }

export class PaymentNextPrevModel extends PaymentsNextPrevDto { }

export class TellerOnlineFilteredPaymentsResponseModel extends TellerOnlineFilteredResponseModel<PaymentOverviewModel> { }
